import styled, { css, keyframes } from "styled-components";

const skeletonShimmer = keyframes`
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
`;

const toolbarButtonStyles = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: #858faf;
	cursor: pointer;
	flex-shrink: 0;
	transition:
		background 0.15s ease,
		color 0.15s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.06);
		color: #e5f0fe;
	}

	&:focus-visible {
		outline: 2px solid #479afe;
		outline-offset: 2px;
	}

	svg {
		width: 18px;
		height: 18px;
	}
`;

export const BettoGameplayPage = styled.div`
	width: 100%;
	min-height: 100%;
	box-sizing: border-box;
	background: #0a0e1a;
	padding: 12px 0 48px;

	@media (min-width: 768px) {
		padding: 16px 0 56px;
	}

	@media (min-width: 1024px) {
		padding: 20px 0 64px;
	}
`;

export const BettoGameplayContent = styled.div`
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
`;

export const BettoGameStage = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	background: #171c2a;
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-radius: 12px;
	overflow: hidden;

	&:fullscreen,
	&:-webkit-full-screen {
		width: 100vw;
		height: 100vh;
		max-width: none;
		border-radius: 0;
		border: none;
		background: #000;
	}

	&:fullscreen iframe,
	&:-webkit-full-screen iframe {
		flex: 1 1 auto;
		min-height: 0;
		height: 100%;
	}

	${({ $fullWidth }) =>
		$fullWidth &&
		css`
			min-height: calc(100vh - var(--bt-header-height, 62px) - 120px);
		`}

	${({ $landscape }) =>
		$landscape &&
		css`
			@media (orientation: landscape) and (max-width: 1024px) {
				max-width: calc(100% - 8px);
			}
		`}
`;

export const BettoGameFrameWrap = styled.div`
	position: relative;
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	min-height: min(56vh, calc(100vh - var(--bt-header-height, 62px) - 220px));

	@media (min-width: 768px) {
		min-height: min(62vh, calc(100vh - var(--bt-header-height, 62px) - 240px));
	}

	@media (min-width: 1024px) {
		min-height: min(68vh, calc(100vh - var(--bt-header-height, 62px) - 260px));
	}

	${({ $fullWidth }) =>
		$fullWidth &&
		css`
			min-height: calc(100vh - var(--bt-header-height, 62px) - 100px);
		`}
`;

export const BettoGameStageSkeleton = styled.div`
	position: relative;
	flex: 1 1 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: min(56vh, calc(100vh - var(--bt-header-height, 62px) - 220px));
	background-color: #171c2a;
	background-image: linear-gradient(
		100deg,
		rgba(255, 255, 255, 0) 30%,
		rgba(82, 151, 255, 0.12) 50%,
		rgba(255, 255, 255, 0) 70%
	);
	background-size: 200% 100%;
	background-repeat: no-repeat;
	animation: ${skeletonShimmer} 1.4s ease-in-out infinite;

	& > * {
		position: relative;
		z-index: 1;
	}

	@media (min-width: 768px) {
		min-height: min(62vh, calc(100vh - var(--bt-header-height, 62px) - 240px));
	}

	@media (min-width: 1024px) {
		min-height: min(68vh, calc(100vh - var(--bt-header-height, 62px) - 260px));
	}

	${({ $fullWidth }) =>
		$fullWidth &&
		css`
			min-height: calc(100vh - var(--bt-header-height, 62px) - 100px);
		`}
`;

export const BettoGameIframe = styled.iframe`
	display: block;
	width: 100%;
	flex: 1 1 auto;
	min-height: inherit;
	border: 0;
	background: #000;
`;

export const BettoIosCloseButton = styled.div`
	position: fixed;
	top: 8px;
	right: 8px;
	z-index: 2147483641;
	display: none;
	border-radius: 8px;
	border: 1px solid rgba(255, 255, 255, 0.12);
	width: 36px;
	height: 36px;
	padding: 6px;
	justify-content: center;
	align-items: center;
	background: #1f2536;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);

	&.iframe-fullscreen-ios {
		display: flex;
	}
`;

export const BettoGameToolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 8px 12px;
	background: #1f2536;
	border-top: 1px solid rgba(255, 255, 255, 0.06);
	position: relative;
	z-index: 2;
	flex-shrink: 0;

	@media (min-width: 768px) {
		padding: 10px 16px;
	}
`;

export const BettoToolbarGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
`;

export const BettoToolbarActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;

	.share-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 36px;
		min-width: max-content;
		padding: 0 12px;
		margin-right: 0;
		border: none;
		border-radius: 8px;
		background: linear-gradient(90deg, #0251b0 0%, #0362d4 100%);
		color: #fff;
		font-family: inherit;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.02em;
		cursor: pointer;
		flex-shrink: 0;
		transition: opacity 0.15s ease;

		&:hover:not(:disabled) {
			opacity: 0.9;
		}

		&:disabled {
			opacity: 0.45;
			cursor: not-allowed;
		}

		svg {
			width: 16px;
			height: 16px;
			margin-right: 6px;
		}

		@media (max-width: 767px) {
			width: 36px;
			min-width: 36px;
			padding: 0;

			svg {
				margin-right: 0;
			}
		}
	}
`;

export const BettoToolbarButton = styled.button`
	${toolbarButtonStyles}

	${({ $active }) =>
		$active &&
		css`
			color: #479afe;
		`}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
		pointer-events: none;
	}
`;

export const BettoGameMeta = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	padding: 16px 0 8px;

	@media (min-width: 768px) {
		flex-direction: row;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 8px 16px;
		padding: 20px 0 12px;
	}
`;

export const BettoGameTitle = styled.h1`
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.3;
	color: #e5f0fe;

	@media (min-width: 768px) {
		font-size: 22px;
	}
`;

export const BettoGameProvider = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: #479afe;
`;

export const BettoRecommendations = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	margin-top: 8px;
`;

export const BettoGameplayBelowFold = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	margin-top: 8px;
`;
